import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import LangSelectorDropdown from 'component/LangSelectorDropdown';
import languages from 'constants/languages';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import {
	addGlossaryEntry,
	deleteGlossaryEntry,
	setGlossaryItems,
	updateGlossaryEntry,
	updateGlossaryId,
	addGlossaryEntries,
	updateGlossariesIds,
} from 'store/glossary';
import { groupByLanguagePair, parseExcelToJson } from './helper';
import { createNotification } from 'utils/create-notification';
import GlossaryService from 'services/glossary-services';
import Modal from '.';
import Papa from 'papaparse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

const requiredGlossaryFields = [
	'sourceText',
	'targetText',
	'sourceLanguage',
	'targetLanguage',
];

function validateGlossaryFields(fileFields) {
	if (!fileFields || !Array.isArray(fileFields)) {
		return {
			error: `File must have headers with the following fields: ${requiredGlossaryFields.join(
				', '
			)}`,
		};
	}

	const missingFields = requiredGlossaryFields.filter(
		(requiredField) => !fileFields.includes(requiredField)
	);

	if (missingFields.length > 0) {
		return {
			error: `Missing required fields: ${missingFields.join(
				', '
			)}. Required fields are: ${requiredGlossaryFields.join(', ')}`,
		};
	}

	if (fileFields.length !== requiredGlossaryFields.length) {
		return {
			error: `File should have exactly ${
				requiredGlossaryFields.length
			} fields: ${requiredGlossaryFields.join(', ')}`,
		};
	}

	return true;
}

function GlossaryModal({ onClose, isOpen }) {
	const [sourceLang, setSourceLang] = useState('en');
	const [targetLang, setTargetLang] = useState('fr');
	const [sourceText, setSourceText] = useState('');
	const [targetText, setTargetText] = useState('');
	const [editingIndex, setEditingIndex] = useState(null);
	const [editedSource, setEditedSource] = useState('');
	const [editedTarget, setEditedTarget] = useState('');
	const [loading, setLoading] = useState(false);
	const [fileName, setFileName] = useState('');
	const fileInputRef = useRef(null);

	const dispatch = useDispatch();
	const glossary = useSelector((state) => state.glossary);
	const groupedGlossary = glossary ? groupByLanguagePair(glossary) : {};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!sourceText || !targetText) {
			createNotification('error', 'Error', 'Please fill all fields');
			return;
		}
		const newEntry = {
			sourceLanguage: sourceLang,
			sourceText,
			targetLanguage: targetLang,
			targetText,
		};
		setSourceText('');
		setTargetText('');
		try {
			dispatch(addGlossaryEntry({ ...newEntry, _id: 'temp' }));
			const response = await GlossaryService.addGlossary(newEntry);
			dispatch(
				updateGlossaryId({ id: 'temp', newId: response?.data?.data?._id })
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handleSaveEdit = async (index) => {
		if (!editedSource || !editedTarget) {
			createNotification('error', 'Error', 'Please fill all fields');
			return;
		}
		if (
			editedSource === glossary[index].sourceText &&
			editedTarget === glossary[index].targetText
		) {
			setEditingIndex(null);
			return;
		}

		const updatedEntry = {
			...glossary[index],
			sourceText: editedSource,
			targetText: editedTarget,
		};

		dispatch(updateGlossaryEntry({ index, updatedEntry }));

		try {
			const response = await GlossaryService.updateGlossary({
				id: glossary[index]._id,
				sourceText: editedSource,
				targetText: editedTarget,
			});
		} catch (error) {
			console.log(error);
		}

		setEditingIndex(null);
	};

	const handleDelete = async (id) => {
		dispatch(deleteGlossaryEntry({ id }));
		try {
			const response = await GlossaryService.delGlossary(id);
		} catch (error) {
			console.log(error);
		}
	};

	const handleClick = () => fileInputRef.current.click();
	const handleChange = async (e) => {
		const file = e.target.files[0];
		if (file) setFileName(file.name);

		if (file?.name.endsWith('.csv')) {
			handleCsvFile(file);
		} else if (file?.name.endsWith('.xlsx') || file?.name.endsWith('.xls')) {
			handleXlsxFIle(file);
		} else {
			createNotification(
				'error',
				'Error',
				'Please Upload a valid XLSX or CSV file'
			);
			setFileName('');
			fileInputRef.current.value = '';
		}
	};

	const handleCsvFile = async (file) => {
		try {
			const results = await new Promise((resolve, reject) => {
				Papa.parse(file, {
					header: true, // Use first row as headers
					skipEmptyLines: true, // Skip empty lines
					complete: (result) => resolve(result),
					error: (error) => {
						reject(error);
					},
				});
			});

			if (!results.meta.fields || results.meta.fields.length === 0) {
				createNotification(
					'error',
					'Error',
					'CSV file must have headers in the first row'
				);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			const isFieldsAccurate = validateGlossaryFields(results?.meta?.fields);

			if (isFieldsAccurate !== true && isFieldsAccurate.error) {
				createNotification('error', 'Error', isFieldsAccurate.error);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			if (!results.data || results.data.length === 0) {
				createNotification(
					'error',
					'Error',
					'CSV file must contain data rows after headers'
				);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			if (results.errors.length > 0) {
				createNotification('error', 'Error', 'Error parsing CSV: ');
				console.log(`Parsing errors: ${JSON.stringify(results?.errors)}`);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			dispatch(addGlossaryEntries(results.data));

			const response = await GlossaryService.addGlossaryEntries({
				glossaries: results.data,
			});
			if (response?.data?.data) {
				dispatch(updateGlossariesIds(response?.data?.data));
				createNotification('success', 'Success', 'CSV Added Successfully');
			}
		} catch (error) {
			createNotification(
				'error',
				'Error',
				`Please Upload a valid CSV file, ${error?.message}`
			);
			setFileName('');
			fileInputRef.current.value = '';
			console.error('Error parsing CSV:', error, error.message);
		}
	};

	const handleXlsxFIle = async (file) => {
		try {
			const result = await parseExcelToJson(file);

			// Verify headers are present
			if (!result.headers || result.headers.length === 0) {
				createNotification(
					'error',
					'Error',
					'XLSX file must have headers in the first row'
				);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			// Verify required fields
			const isFieldsAccurate = validateGlossaryFields(result.headers);

			if (isFieldsAccurate !== true && isFieldsAccurate.error) {
				createNotification('error', 'Error', isFieldsAccurate.error);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			// Verify data rows exist
			if (
				!result.data ||
				!Array.isArray(result.data) ||
				result.data.length === 0
			) {
				createNotification(
					'error',
					'Error',
					'XLSX file must contain data rows after headers'
				);
				setFileName('');
				fileInputRef.current.value = '';
				return;
			}

			dispatch(addGlossaryEntries(result.data));

			const response = await GlossaryService.addGlossaryEntries({
				glossaries: result.data,
			});
			if (response?.data?.data) {
				dispatch(updateGlossariesIds(response?.data?.data));
				createNotification('success', 'Success', 'XlSX Added Successfully');
			}
		} catch (error) {
			createNotification(
				'error',
				'Error',
				`Please Upload a valid XLSX file, ${error?.message}`
			);
			setFileName('');
			fileInputRef.current.value = '';
			console.log(error);
		}
	};

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await GlossaryService.getGlossary();
				dispatch(setGlossaryItems(response?.data.data));
				setLoading(false);
			} catch (error) {
				console.log(error);
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	return (
		<Modal open={true} handleClose={onClose} title="Glossary" className={`z-0`}>
			<form onSubmit={handleSubmit}>
				<div className="bg-[#F5F5F5] p-4 space-y-4">
					<div className="flex ">
						<LangSelectorDropdown
							defaultLang="en"
							onChange={(lang) => setSourceLang(lang)}
						/>
						<input
							type="text"
							value={sourceText}
							onChange={(e) => setSourceText(e.target.value)}
							className="border border-l-0 border-gray-300 rounded rounded-l-none py-0.5 w-full px-4"
						/>
					</div>
					<div className="flex">
						<LangSelectorDropdown
							defaultLang="fr"
							onChange={(lang) => setTargetLang(lang)}
						/>
						<input
							type="text"
							value={targetText}
							onChange={(e) => setTargetText(e.target.value)}
							className="border border-l-0 border-gray-300 rounded rounded-l-none py-0.5 w-full px-4"
						/>
					</div>
					<div className="flex justify-end gap-3">
						<div className="relative flex">
							{fileName && (
								<div className="flex gap-2 items-center  border-2 rounded-lg px-2  mr-2">
									<p className=" text-lg font-semibold">{fileName}</p>
									<FontAwesomeIcon
										icon={faXmark}
										className="cursor-pointer h-5 w-5 text-red-600 hover:text-red-400"
										onClick={() => {
											setFileName('');
											fileInputRef.current.value = '';
										}}
									/>
								</div>
							)}
							<input
								type="file"
								accept=".csv, .xlsx, .xls"
								ref={fileInputRef}
								onChange={handleChange}
								className="hidden"
							/>
							<button
								onClick={handleClick}
								type="button"
								className="bg-[#6DB7F2] hover:bg-[#56a4e4] text-white px-4 py-2 rounded"
							>
								Upload File
							</button>
						</div>
						<button
							type="submit"
							className="bg-[#6DB7F2] hover:bg-[#56a4e4] text-white px-4 py-2 rounded "
						>
							Add
						</button>
					</div>
				</div>
			</form>

			{loading ? (
				<div className="flex justify-center items-center h-full w-full mt-4">
					<div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
				</div>
			) : (
				<>
					{glossary && glossary.length > 0 ? (
						Object.entries(groupedGlossary).map(([key, entries]) => {
							const [source, target] = key.split('/');

							return (
								<div key={key} className="m-4">
									<div className="flex gap-4 font-semibold text-lg items-center">
										<p>
											{
												// languages.find((lang) => lang.code === source)?.name ||
												source.toUpperCase()
											}
										</p>
										{/* <p>{entries[0].sourceLanguage.toUpperCase()}</p> */}
										<ArrowForwardIcon />
										<p>
											{
												// languages.find((lang) => lang.code === target)?.name ||
												target.toUpperCase()
											}
										</p>
									</div>

									<div className="space-y-1">
										{entries.map((entry, idx) => {
											const globalIndex = glossary.findIndex(
												(e) => e._id === entry._id
											);
											const isEditing = editingIndex === globalIndex;

											return (
												<div className="" key={idx}>
													{isEditing ? (
														<>
															<div className="flex items-center gap-3">
																<div className="w-1/2 flex justify-between items-center">
																	<div className="w-full flex justify-between items-center">
																		<input
																			value={editedSource}
																			onChange={(e) =>
																				setEditedSource(e.target.value)
																			}
																			className="border border-gray-500 w-full rounded px-1  py-1"
																		/>
																		<ArrowForwardIcon className="text-gray-400 w-max" />
																	</div>
																</div>
																<input
																	value={editedTarget}
																	onChange={(e) =>
																		setEditedTarget(e.target.value)
																	}
																	className="border border-gray-500 w-1/2 rounded px-1  py-1"
																/>
																<div className="flex gap-2">
																	<button
																		className="p-2 hover:bg-gray-200 rounded"
																		onClick={() => {
																			handleSaveEdit(globalIndex);
																		}}
																	>
																		<DoneIcon />
																	</button>
																	<button
																		className="p-2 hover:bg-gray-200 rounded"
																		onClick={() => setEditingIndex(null)}
																	>
																		<ClearIcon />
																	</button>
																</div>
															</div>
														</>
													) : (
														<div className="flex items-center gap-3">
															<div className="w-1/2 flex justify-between items-center">
																<p className="w-full">{entry.sourceText}</p>
																<ArrowForwardIcon className="text-gray-400 w-max" />
															</div>
															<p className="w-1/2">{entry.targetText}</p>
															<div className="flex gap-2">
																<button
																	className="p-2 hover:bg-gray-200 rounded"
																	onClick={() => {
																		setEditingIndex(globalIndex);
																		setEditedSource(entry.sourceText);
																		setEditedTarget(entry.targetText);
																	}}
																>
																	<EditIcon />
																</button>
																<button
																	className="p-2 hover:bg-gray-200 rounded"
																	onClick={() => handleDelete(entry._id)}
																>
																	<DeleteOutlineIcon />
																</button>
															</div>
														</div>
													)}
												</div>
											);
										})}
									</div>
								</div>
							);
						})
					) : (
						<div className="mt-3">
							<p>
								Define how specific words or phrases should be translated, and
								LetzChat will automatically apply your preferences to all
								translations.
							</p>
							<div className="mt-2">
								<p>Such as:</p>
								<div className="space-y-3 mt-3">
									<div className="flex space-x-5">
										<div className="flex justify-between w-1/2">
											<p className="w-12">Backend</p>
											<p className="text-gray-400">To</p>
										</div>

										<p className="w-1/2">
											Backend
											<span className="text-gray-400">
												(Don't translate as its a term in tech)
											</span>
										</p>
									</div>
									<div className="flex space-x-5">
										<div className="flex justify-between w-1/2">
											<p className="w-12">Bug</p>
											<p className="text-gray-400">To</p>
										</div>
										<p className="w-1/2">
											Anomalie{' '}
											<span className="text-gray-400">
												(Formal term; “bug” might be misunderstood)
											</span>
										</p>
									</div>
								</div>
							</div>
						</div>
					)}{' '}
				</>
			)}
		</Modal>
	);
}

export default GlossaryModal;
